import * as React from 'react';

type Row = Readonly<{ value: string; disabled?: boolean }>;

const STEPS = new Set(['ArrowDown', 'ArrowUp', 'Home', 'End']);

/** The row a filter list's keyboard is on: the first of `firstRows` that can be picked, until the arrows, Home, End or the pointer move it, and the first again under a new search. */
export function useFilterListCursor(
  rows: ReadonlyArray<Row>,
  search: string,
  firstRows: ReadonlyArray<Row> = rows,
) {
  const [cursor, setCursor] = React.useState<{ value: string; search: string } | null>(null);
  const pickable = rows.filter((row) => row.disabled !== true);
  const moved =
    cursor !== null && cursor.search === search
      ? pickable.find((row) => row.value === cursor.value)
      : undefined;
  // A search with no matches leaves the keyboard on no row, rather than on a pick listed above the matches.
  const active =
    moved ??
    firstRows.find((row) => row.disabled !== true) ??
    (firstRows === rows ? pickable[0] : undefined);

  return {
    activeValue: active?.value,
    moveTo: (value: string) => {
      if (cursor?.value !== value || cursor.search !== search) setCursor({ value, search });
    },
    /** The row a navigation key lands on, or undefined for any other key. */
    stepFor: (key: string): Row | undefined => {
      if (!STEPS.has(key) || pickable.length === 0) return undefined;
      const index = active === undefined ? -1 : pickable.indexOf(active);
      if (key === 'Home') return pickable[0];
      if (key === 'End') return pickable.at(-1);
      if (key === 'ArrowDown') return pickable[Math.min(index + 1, pickable.length - 1)];
      return pickable[Math.max(index - 1, 0)];
    },
  };
}
